import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BarChart3, CheckCircle, Eye, X, Loader2, Search, LayoutGrid, List, Sparkles, CheckCircle2, AlertCircle, Pencil, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { addPageToFunnel, addPagesBatchToFunnel, removePageFromFunnel, removePagesBatchFromFunnel, updateFunnel, getTranslateV2StatusByFunnel } from '@/services/api/funnel';
import { getMetaPages } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { LANGUAGES } from '@/pages/Meta/Connection/constants/config-options';
import type { FunnelDetailResponse, FunnelPageInfo } from '@/types/funnel';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';
import ValidateTranslationModal from './ValidateTranslationModal';

type FilterType = 'pages' | 'category';
type TranslationStatus = 'idle' | 'translating' | 'done' | 'failed' | 'cancelled';

// ─── AddPagesPopover ──────────────────────────────────────────────────────────
function AddPagesPopover({
    funnelId,
    linkedPageIds,
    onAdded,
}: {
    funnelId: number;
    linkedPageIds: Set<string>;
    onAdded: () => void;
}) {
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [allPages, setAllPages] = useState<MetaPage[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState<FilterType>('pages');
    const [activeCategories, setActiveCategories] = useState<Set<number>>(new Set());
    const [toggling, setToggling] = useState<number | null>(null);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        getMetaPages()
            .then(data => {
                setAllPages(data.filter(p => p.is_active));
            })
            .finally(() => setLoading(false));
    }, [open]);

    const normalize = (s: string) =>
        s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

    const categories = Array.from(
        new Map(
            allPages
                .filter(p => p.internal_category_id && p.internal_category_name)
                .map(p => [p.internal_category_id as number, p.internal_category_name as string])
        ).entries()
    ).map(([id, name]) => ({ id, name }));

    // Filtra por busca/categoria
    const filtered = allPages.filter(p => {
        if (filterType === 'category' && activeCategories.size > 0)
            return activeCategories.has(p.internal_category_id as number);
        if (filterType === 'pages' && search.trim()) {
            const t = normalize(search);
            return normalize(p.page_name).includes(t) || p.page_id.includes(search.trim());
        }
        return true;
    });

    // Ordena: selecionadas primeiro
    const sorted = [...filtered].sort((a, b) => {
        const aLinked = linkedPageIds.has(a.page_id) ? 0 : 1;
        const bLinked = linkedPageIds.has(b.page_id) ? 0 : 1;
        return aLinked - bLinked;
    });

    const [addingAll, setAddingAll] = useState(false);

    const toggleCategory = async (id: number) => {
        const categoryPages = allPages.filter(p => p.internal_category_id === id);
        const allLinked = categoryPages.every(p => linkedPageIds.has(p.page_id));
        setAddingAll(true);
        try {
            if (allLinked) {
                // Remove todas via batch (1 request) - p.id aqui é internal_page_id
                const result = await removePagesBatchFromFunnel(funnelId, { internal_page_ids: categoryPages.map(p => p.id) });
                toast({ title: `${result.removed} página${result.removed !== 1 ? 's' : ''} removida${result.removed !== 1 ? 's' : ''}` });
            } else {
                // Adiciona todas via batch (1 request)
                const toAdd = categoryPages.filter(p => !linkedPageIds.has(p.page_id));
                const result = await addPagesBatchToFunnel(funnelId, toAdd.map(p => p.id));
                toast({ title: `${result.added} página${result.added !== 1 ? 's' : ''} adicionada${result.added !== 1 ? 's' : ''}` });
            }
            onAdded();
        } finally {
            setAddingAll(false);
        }
    };

    const handleTogglePage = async (page: MetaPage) => {
        setToggling(page.id);
        const isLinked = linkedPageIds.has(page.page_id);
        try {
            if (isLinked) {
                await removePageFromFunnel(funnelId, page.id);
                toast({ title: `"${page.page_name}" removida` });
            } else {
                await addPageToFunnel(funnelId, page.id);
                toast({ title: `"${page.page_name}" adicionada` });
            }
            onAdded();
        } catch (e: any) {
            toast({ variant: 'destructive', title: e.response?.data?.detail || 'Erro' });
        } finally {
            setToggling(null);
        }
    };

    return (
        <Popover open={open} onOpenChange={v => { setOpen(v); if (!v) { setSearch(''); setActiveCategories(new Set()); } }}>
            <PopoverTrigger asChild>
                <button className="flex items-center gap-0.5 h-7 px-1.5 text-gray-400 hover:text-gray-700 border border-gray-200 rounded hover:border-gray-400 transition-colors shrink-0 bg-white"
                    title="Filtrar / Adicionar páginas">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    <svg className="w-2.5 h-2.5 -ml-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 shadow-lg" align="start" side="bottom">
                {/* Toggle pages / categoria */}
                <div className="flex border-b border-gray-100">
                    {(['pages', 'category'] as FilterType[]).map(type => (
                        <button
                            key={type}
                            onClick={() => setFilterType(type)}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                                filterType === type
                                    ? 'text-gray-900 border-b-2 border-gray-800'
                                    : 'text-gray-400 hover:text-gray-600'
                            }`}
                        >
                            {type === 'pages' ? <List className="w-3.5 h-3.5" /> : <LayoutGrid className="w-3.5 h-3.5" />}
                            {type === 'pages' ? 'Páginas' : 'Categoria'}
                        </button>
                    ))}
                </div>

                {filterType === 'pages' && (
                    <div className="p-3 border-b border-gray-100">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                                autoFocus
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Buscar página..."
                                className="w-full h-8 pl-8 pr-3 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300"
                            />
                        </div>
                    </div>
                )}

                {filterType === 'category' && (
                    <div className="p-3 border-b border-gray-100">
                        {categories.length === 0 ? (
                            <p className="text-xs text-gray-400">Nenhuma categoria cadastrada</p>
                        ) : (
                            <div className="flex flex-wrap gap-1.5">
                                {categories.map(c => {
                                    const catPages = allPages.filter(p => p.internal_category_id === c.id);
                                    const allLinked = catPages.length > 0 && catPages.every(p => linkedPageIds.has(p.page_id));
                                    return (
                                        <button
                                            key={c.id}
                                            onClick={() => toggleCategory(c.id)}
                                            disabled={addingAll}
                                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1 disabled:opacity-50 ${
                                                allLinked
                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                                            }`}
                                        >
                                            {addingAll && <Loader2 className="w-3 h-3 animate-spin" />}
                                            {c.name}
                                            {allLinked && !addingAll && <span className="text-[10px]">✓</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

<div className="max-h-64 overflow-y-auto">
                    {loading ? (
                        <div className="flex justify-center py-6">
                            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                        </div>
                    ) : sorted.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-6">Nenhuma página encontrada</p>
                    ) : (
                        sorted.map(page => {
                            const isLinked = linkedPageIds.has(page.page_id);
                            return (
                                <button
                                    key={page.id}
                                    onClick={() => handleTogglePage(page)}
                                    disabled={toggling === page.id}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left disabled:opacity-50 ${
                                        isLinked ? 'bg-blue-50/50' : ''
                                    }`}
                                >
                                    {/* Checkbox */}
                                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                        isLinked ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                                    }`}>
                                        {isLinked && (
                                            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>

                                    {toggling === page.id ? (
                                        <Loader2 className="w-7 h-7 animate-spin text-blue-500 shrink-0" />
                                    ) : (
                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isLinked ? 'bg-blue-600' : 'bg-gray-700'}`}>
                                            <span className="text-white font-semibold text-[9px]">
                                                {page.page_name.substring(0, 2).toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className={`text-xs font-medium truncate ${isLinked ? 'text-blue-700' : 'text-gray-800'}`}>
                                            {page.page_name}
                                        </p>
                                        {page.internal_category_name && (
                                            <p className="text-[10px] text-gray-400">{page.internal_category_name}</p>
                                        )}
                                        {page.meta_user_name && (
                                            <p className="text-[10px] text-gray-400">Conta: {page.meta_user_name}</p>
                                        )}
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

// ─── Toolbar principal ─────────────────────────────────────────────────────────
interface ToolbarProps {
    funnelName: string;
    onBack: () => void;
    onValidate: () => void;
    onPreview?: () => void;
    onDetails?: () => void;
    funnel?: FunnelDetailResponse;
    onRename?: (name: string) => Promise<void>;
}

export default function Toolbar({ funnelName, onBack, onValidate, onPreview, onDetails, funnel, onRename }: ToolbarProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [aiTranslate, setAiTranslate] = useState(!!funnel?.ai_translate);
    const [sourceLanguage, setSourceLanguage] = useState(funnel?.source_language || 'Português - Brasil');
    const [validateOpen, setValidateOpen] = useState(false);
    const [isRenamingName, setIsRenamingName] = useState(false);
    const [editingName, setEditingName] = useState(funnelName);
    const [isSavingName, setIsSavingName] = useState(false);
    const nameInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => { setEditingName(funnelName); }, [funnelName]);

    const handleRenameCommit = async () => {
        const trimmed = editingName.trim();
        if (!trimmed || trimmed === funnelName || !onRename) {
            setEditingName(funnelName);
            setIsRenamingName(false);
            return;
        }
        setIsSavingName(true);
        try {
            await onRename(trimmed);
        } finally {
            setIsSavingName(false);
            setIsRenamingName(false);
        }
    };
    const [translationStatus, setTranslationStatus] = useState<TranslationStatus>('idle');
    const [translationProgress, setTranslationProgress] = useState({ done: 0, total: 0, skipped: 0 });
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => { setAiTranslate(!!funnel?.ai_translate); }, [funnel?.ai_translate]);
    useEffect(() => { setSourceLanguage(funnel?.source_language || 'Português - Brasil'); }, [funnel?.source_language]);

    const invalidate = () => {
        if (funnel) queryClient.invalidateQueries({ queryKey: ['funnel-details', String(funnel.id)] });
    };

    const stopPolling = useCallback(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, []);

    const pollTranslation = useCallback((startImmediately = false) => {
        if (!funnel) return;
        stopPolling();

        const doCheck = async (isFirst = false) => {
            try {
                // Engine V2 (Tarefa 2): resolve o run_id mais recente do funil
                // no ms-meta-ops via funnel_translate_v2_latest, sem fallback de
                // recomputar do banco (diferente da V1) — se não há run ativo,
                // retorna status "idle" diretamente.
                const data = await getTranslateV2StatusByFunnel(funnel.id);
                const done = parseInt(data.pages_done || '0', 10);
                const total = parseInt(data.pages_total || '0', 10);
                const skipped = parseInt(data.pages_skipped || '0', 10);
                setTranslationProgress({ done, total, skipped });

                if (data.status === 'running' || data.status === 'queued') {
                    setTranslationStatus('translating');
                    // Garante que o interval está rodando
                    if (!pollRef.current) {
                        pollRef.current = setInterval(() => doCheck(false), 3000);
                    }
                } else if (data.status === 'completed' || data.status === 'partial') {
                    stopPolling();
                    if (!isFirst || startImmediately) {
                        // status 'done' na Toolbar não distingue completed/partial
                        // hoje (badge único de sucesso) — o detalhe fica no
                        // ValidateTranslationModal, que já trata partial à parte.
                        setTranslationStatus('done');
                        doneTimerRef.current = setTimeout(() => setTranslationStatus('idle'), 6000);
                        invalidate();
                    } else {
                        setTranslationStatus('idle');
                    }
                } else if (data.status === 'failed') {
                    stopPolling();
                    setTranslationStatus('failed');
                    doneTimerRef.current = setTimeout(() => setTranslationStatus('idle'), 6000);
                } else if (data.status === 'cancelled') {
                    stopPolling();
                    setTranslationStatus('cancelled');
                    doneTimerRef.current = setTimeout(() => setTranslationStatus('idle'), 4000);
                } else {
                    stopPolling();
                    setTranslationStatus('idle');
                }
            } catch {
                // silently retry
            }
        };

        // Primeira checagem — só inicia interval se status for ativo
        doCheck(true);
    }, [funnel?.id, stopPolling]);

    // Ao montar, se ai_translate está ativo, verifica se há tradução em andamento
    useEffect(() => {
        if (funnel?.ai_translate) {
            pollTranslation();
        }
        return () => {
            stopPolling();
            if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
        };
    }, [funnel?.id, funnel?.ai_translate]);

    const updateMutation = useMutation({
        mutationFn: (data: Parameters<typeof updateFunnel>[1]) => updateFunnel(funnel!.id, data),
        onSuccess: invalidate,
        onError: () => toast({ variant: 'destructive', title: 'Erro ao salvar configurações' }),
    });

    const removeMutation = useMutation({
        mutationFn: (fp: FunnelPageInfo) => removePageFromFunnel(funnel!.id, fp.id),
        onSuccess: invalidate,
        onError: () => toast({ variant: 'destructive', title: 'Erro ao remover página' }),
    });

    const removeAllMutation = useMutation({
        mutationFn: (funnel_page_ids: number[]) => removePagesBatchFromFunnel(funnel!.id, { funnel_page_ids }),
        onSuccess: invalidate,
        onError: () => toast({ variant: 'destructive', title: 'Erro ao remover páginas' }),
    });

    const handleToggle = (val: boolean) => {
        setAiTranslate(val);
        const payload: Parameters<typeof updateFunnel>[1] = { ai_translate: val };
        if (val && !funnel?.source_language) {
            payload.source_language = sourceLanguage || 'Português - Brasil';
        }
        updateMutation.mutate(payload, {
            onSuccess: () => {
                if (val && funnel) {
                    setTranslationStatus('translating');
                    setTranslationProgress({ done: 0, total: 0, skipped: 0 });
                    setTimeout(() => pollTranslation(true), 2000);
                } else if (!val) {
                    setTranslationStatus('idle');
                    stopPolling();
                }
            }
        });
    };

    const handleSourceLang = (val: string) => {
        setSourceLanguage(val);
        updateMutation.mutate({ source_language: val || undefined });
    };

    const linkedPageIds = new Set(funnel?.pages.map(p => p.page_id) ?? []);

    return (
        <div className="bg-white border-b border-gray-200 flex-shrink-0">
            {/* Row 1: navegação + ações */}
            <div className="h-14 flex items-center justify-between px-6">
                {/* Left */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={onBack} className="text-gray-600 hover:text-gray-900">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Voltar
                    </Button>
                    <div className="h-6 w-px bg-gray-300" />
                    <div>
                        {onRename && isRenamingName ? (
                            <input
                                ref={nameInputRef}
                                className="text-base font-semibold text-gray-900 border-b border-gray-400 outline-none bg-transparent w-56"
                                value={editingName}
                                onChange={e => setEditingName(e.target.value)}
                                onBlur={handleRenameCommit}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') nameInputRef.current?.blur();
                                    if (e.key === 'Escape') { setEditingName(funnelName); setIsRenamingName(false); }
                                }}
                                disabled={isSavingName}
                                autoFocus
                            />
                        ) : (
                            <div className="flex items-center gap-1.5 group">
                                <h1 className="text-base font-semibold text-gray-900">{funnelName}</h1>
                                {onRename && (
                                    <button
                                        type="button"
                                        onClick={() => setIsRenamingName(true)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-600"
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        )}
                        <p className="text-xs text-gray-500">Editor de Fluxo</p>
                    </div>
                </div>

                {/* Right */}
                <div className="flex items-center gap-3">
                    <Badge variant="outline" className="gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        Autoguardando
                    </Badge>

                    {onPreview && (
                        <Button variant="outline" size="sm" onClick={onPreview} className="gap-2">
                            <Eye className="w-4 h-4" />
                            Preview
                        </Button>
                    )}

                    {onDetails && (
                        <Button variant="outline" size="sm" onClick={onDetails} className="gap-2">
                            <BarChart3 className="w-4 h-4" />
                            Detalhes
                        </Button>
                    )}

                    <Button variant="outline" size="sm" onClick={onValidate} className="gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Validar Funil
                    </Button>
                </div>
            </div>

            {/* Row 2: páginas + tradução (só quando temos dados do funil) */}
            {funnel && (
                <div className="min-h-[40px] border-t border-gray-100 flex items-center px-6 gap-4 py-1.5">

                    {/* ── PÁGINAS ── */}
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-500 shrink-0">
                            Páginas
                        </span>

                        <div className="flex items-center gap-1 min-w-0 flex-1">
                            {/* Filtro / Adicionar integrado */}
                            <AddPagesPopover funnelId={funnel.id} linkedPageIds={linkedPageIds} onAdded={invalidate} />

                            {/* Chips das páginas selecionadas (máximo 5 visíveis) */}
                            <div className="flex items-center gap-1 min-w-0">
                                {funnel.pages.slice(0, 5).map(page => (
                                    <span key={page.id}
                                        className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs font-medium pl-2.5 pr-1.5 py-1 rounded whitespace-nowrap">
                                        <button
                                            onClick={() => removeMutation.mutate(page)}
                                            disabled={removeMutation.isPending}
                                            className="text-gray-400 hover:text-red-500 transition-colors mr-0.5"
                                            title="Remover página"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                        {page.page_name || page.page_id}
                                    </span>
                                ))}
                            </div>

                            {/* Limpar tudo */}
                            {funnel.pages.length > 1 && (
                                <button
                                    onClick={() => removeAllMutation.mutate(funnel.pages.map(p => p.id))}
                                    className="text-gray-300 hover:text-gray-500 transition-colors shrink-0 p-0.5"
                                    title="Remover todas"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>

                        {/* Contador */}
                        {funnel.pages.length > 0 && (
                            <span className="shrink-0 text-[11px] font-bold bg-blue-500 text-white px-2 py-0.5 rounded-full">
                                {funnel.pages.length}
                            </span>
                        )}
                        <span className="text-[11px] text-gray-400 shrink-0">pág.</span>
                    </div>

                    <div className="h-5 w-px bg-gray-200 shrink-0" />

                    {/* ── TRADUÇÃO I.A ── */}
                    <div className="flex items-center gap-2.5 shrink-0">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            Tradução I.A
                        </span>

                        {/* Toggle custom (sem depender de bg-primary) */}
                        <button
                            role="switch"
                            aria-checked={aiTranslate}
                            onClick={() => handleToggle(!aiTranslate)}
                            disabled={updateMutation.isPending}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                                aiTranslate ? 'bg-cyan-500' : 'bg-gray-300'
                            }`}
                        >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${
                                aiTranslate ? 'translate-x-[18px]' : 'translate-x-[3px]'
                            }`} />
                        </button>

                        {/* Status tag */}
                        {aiTranslate && translationStatus === 'idle' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-cyan-50 text-cyan-700 border border-cyan-200 px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                                Ativo
                            </span>
                        )}
                        {translationStatus === 'translating' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                {translationProgress.total > 0
                                    ? `Traduzindo ${translationProgress.done}/${translationProgress.total}`
                                    : 'Traduzindo...'}
                                {translationProgress.skipped > 0 && (
                                    <span className="text-amber-500 ml-0.5">({translationProgress.skipped} ignoradas)</span>
                                )}
                            </span>
                        )}
                        {translationStatus === 'done' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full">
                                <CheckCircle2 className="w-3 h-3" />
                                Tradução concluída
                            </span>
                        )}
                        {translationStatus === 'failed' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full">
                                <AlertCircle className="w-3 h-3" />
                                Falha na tradução
                            </span>
                        )}
                        {translationStatus === 'cancelled' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-gray-50 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-full">
                                <AlertCircle className="w-3 h-3" />
                                Tradução cancelada
                            </span>
                        )}

                        {/* Idioma de origem */}
                        {aiTranslate && (
                            <SearchableSelect
                                value={sourceLanguage}
                                onValueChange={handleSourceLang}
                                options={[{ value: '', label: 'Idioma de origem (auto)' }, ...LANGUAGES]}
                                searchPlaceholder="Buscar idioma..."
                                className="h-6 text-xs w-48 border-gray-200"
                            />
                        )}

                        {/* Validar tradução */}
                        {aiTranslate && (
                            <button
                                type="button"
                                onClick={() => setValidateOpen(true)}
                                className="inline-flex items-center gap-1 text-[10px] font-medium text-cyan-600 hover:text-cyan-800 border border-cyan-200 hover:border-cyan-400 bg-cyan-50 hover:bg-cyan-100 px-2 py-0.5 rounded-full transition-colors"
                                title="Verificar cobertura de tradução e corrigir divergências"
                            >
                                <ShieldCheck className="w-3 h-3" />
                                Validar
                            </button>
                        )}
                    </div>
                </div>
            )}

            {funnel && (
                <ValidateTranslationModal
                    open={validateOpen}
                    onOpenChange={setValidateOpen}
                    funnelId={funnel.id}
                    funnelName={funnel.nome || ''}
                />
            )}
        </div>
    );
}
